import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead, StatusDot } from "@/components/kit";
import { Sparkles, Image as ImageIcon, LinkIcon, Download, Mail, Trophy, RefreshCw, ArrowRight, Globe } from "lucide-react";

const BANNER_PH =
    "https://images.unsplash.com/photo-1664329182766-2f7759d13f78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHx5b3V0dWJlJTIwY2hhbm5lbCUyMGJhbm5lciUyMGFic3RyYWN0JTIwZ2VvbWV0cnl8ZW58MHx8fHwxNzg2MzE3ODMzfDA&ixlib=rb-4.1.0&q=85";
const AVATAR_PH =
    "https://images.unsplash.com/photo-1613698808499-f772ccb4f527?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxwcm9maWxlJTIwYXZhdGFyJTIwcG9ydHJhaXQlMjBuZW9ufGVufDB8fHx8MTc4NjMxNzgzM3ww&ixlib=rb-4.1.0&q=85";

export default function ChannelKit() {
    const qc = useQueryClient();
    const [niche, setNiche] = useState("");
    const [tone, setTone] = useState("energético y profesional");
    const [selected, setSelected] = useState(null);
    const [fromNiche, setFromNiche] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const autoRan = useRef(false);

    const { data: kits = [] } = useQuery({
        queryKey: ["kits"],
        queryFn: async () => (await api.get("/kits")).data,
    });

    const active = kits.find((k) => k.id === selected) || kits[0];

    const generate = useMutation({
        mutationFn: async (overrides) =>
            (await api.post("/kits/generate", { niche, tone, language: "es", ...(overrides || {}) })).data,
        onSuccess: (k) => {
            toast.success("Kit de canal generado por Minimax");
            setSelected(k.id);
            setFromNiche(null);
            qc.invalidateQueries({ queryKey: ["kits"] });
        },
        onError: (e) => {
            setFromNiche(null);
            toast.error(e?.response?.data?.detail || "Fallo generando el kit");
        },
    });

    useEffect(() => {
        const nicheId = searchParams.get("niche_id");
        const nicheName = searchParams.get("niche");
        if (nicheId && nicheName && !autoRan.current) {
            autoRan.current = true;
            setNiche(nicheName);
            setFromNiche(nicheName);
            generate.mutate({ niche: nicheName, niche_id: nicheId });
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line
    }, []);

    const visuals = useMutation({
        mutationFn: async (id) => (await api.post(`/kits/${id}/visuals`)).data,
        onSuccess: () => {
            toast.success("Banner + avatar generados con Nano Banana");
            qc.invalidateQueries({ queryKey: ["kits"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo generando visuales"),
    });

    const connect = useMutation({
        mutationFn: async (k) =>
            (await api.post("/channels/connect", { name: k.channel_name, handle: k.handle, kit_id: k.id }))
                .data,
        onSuccess: () => toast.success("Canal conectado vía OAuth (mock)"),
        onError: () => toast.error("Fallo conectando el canal"),
    });

    const exportKit = useMutation({
        mutationFn: async (k) => {
            const res = await api.get(`/kits/${k.id}/export`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `kit_${(k.handle || k.id).replace("@", "")}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        },
        onSuccess: () => toast.success("Kit exportado: banner, avatar, paleta y títulos en ZIP"),
        onError: () => toast.error("Fallo exportando el kit"),
    });

    const { data: best } = useQuery({
        queryKey: ["best-niche"],
        queryFn: async () => (await api.get("/strategy/best-niche")).data,
        refetchInterval: (q) => (q.state.data?.status === "running" ? 4000 : false),
    });
    const bestRunning = best?.status === "running";
    const bestCur = best?.current;

    const toggleBest = useMutation({
        mutationFn: async (enabled) => (await api.post("/strategy/best-niche/toggle", { enabled })).data,
        onSuccess: (d) => {
            toast.success(d.enabled ? "Selección mensual del mejor nicho ACTIVADA" : "Selección mensual desactivada");
            qc.invalidateQueries({ queryKey: ["best-niche"] });
        },
        onError: () => toast.error("Fallo cambiando el ajuste"),
    });

    const runBest = useMutation({
        mutationFn: async () => (await api.post("/strategy/best-niche/run")).data,
        onSuccess: () => {
            toast.success("darkzOGx está eligiendo el nicho más rentable y creando su kit…");
            qc.invalidateQueries({ queryKey: ["best-niche"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo iniciando la selección"),
    });

    // when the monthly pick finishes, surface its fresh kit + refresh the kit list
    useEffect(() => {
        if (best?.status === "done" && bestCur?.kit_id) {
            qc.invalidateQueries({ queryKey: ["kits"] });
        }
        // eslint-disable-next-line
    }, [best?.status, bestCur?.kit_id]);

    return (
        <div data-testid="kit-page">
            <PageHead
                title="KIT DE CREACIÓN DE CANAL"
                sub="Minimax genera nombre, handle, descripción SEO, paleta, pilares, plan de títulos y visuales. Tú creas el canal y lo conectas por OAuth."
            />

            <Panel
                title="Canal del mes · el más rentable"
                testId="best-niche-panel"
                className="mb-6 border-primary/60"
                right={<Trophy size={15} className="text-primary" />}
            >
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <p className="max-w-xl text-xs text-muted-foreground">
                        Una vez al mes, darkzOGx analiza datos reales de YouTube y fuentes expertas de la web,
                        elige el <span className="text-primary">único nicho más rentable</span> y te crea su
                        kit de canal listo. Tú solo creas la cuenta y la conectas por OAuth para automatizar todo.
                    </p>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                        <label className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground">
                            <input
                                type="checkbox"
                                data-testid="best-niche-toggle"
                                checked={!!best?.enabled}
                                onChange={(e) => toggleBest.mutate(e.target.checked)}
                                className="h-4 w-4 accent-[#39FF14]"
                            />
                            Auto 1×/mes
                        </label>
                        <Btn
                            testId="best-niche-run-btn"
                            onClick={() => runBest.mutate()}
                            disabled={bestRunning || runBest.isPending}
                        >
                            <span className="flex items-center gap-2">
                                <Trophy size={14} />
                                {bestRunning ? "Eligiendo…" : "Elegir el mejor nicho ahora"}
                            </span>
                        </Btn>
                    </div>
                </div>

                {bestRunning && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-primary" data-testid="best-niche-running">
                        <StatusDot color="#00FFFF" pulse /> Analizando YouTube + web, rankeando y creando el kit (~60-90s)…
                    </div>
                )}
                {best?.status === "failed" && best?.error && (
                    <div className="mt-3 border border-destructive/60 p-2 text-xs text-destructive" data-testid="best-niche-error">
                        Falló: {best.error}
                    </div>
                )}

                {bestCur && (
                    <div className="mt-4 border border-primary/40 bg-muted/20 p-4" data-testid="best-niche-current">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
                                {bestCur.month} · ganador
                            </span>
                            {bestCur.adjacent && (
                                <span className="border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider"
                                    style={{ color: "#39FF14", borderColor: "#39FF14" }}>Adyacente</span>
                            )}
                            <span className="ml-auto font-head text-2xl text-primary">{bestCur.score}</span>
                        </div>
                        <h3 className="font-head text-xl leading-tight">{bestCur.niche_name}</h3>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] md:grid-cols-4">
                            <div>Canal: <span className="text-foreground">{bestCur.channel_name || "—"}</span></div>
                            <div>RPM: <span className="text-foreground">{bestCur.rpm_estimate}</span></div>
                            <div>Formato: <span className="uppercase text-primary">{bestCur.best_format}</span></div>
                            <div>Handle: <span className="text-foreground">{bestCur.suggested_handle || "—"}</span></div>
                        </div>
                        {bestCur.evidence && (
                            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{bestCur.evidence}</p>
                        )}
                        {bestCur.suggested_email && (
                            <div className="mt-2 flex items-center gap-2 text-[11px]" data-testid="best-niche-email">
                                <Mail size={11} className="shrink-0 text-primary" />
                                <span className="text-muted-foreground">Email para la cuenta:</span>
                                <span className="break-all font-mono text-primary">{bestCur.suggested_email}</span>
                            </div>
                        )}
                        {(bestCur.sources || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-1 text-[10px]">
                                <Globe size={10} className="text-primary" />
                                {bestCur.sources.slice(0, 4).map((s, i) => (
                                    <a key={i} href={s.uri} target="_blank" rel="noreferrer"
                                        className="max-w-[150px] truncate border border-border px-1.5 py-0.5 text-primary hover:border-primary">
                                        {(() => { try { return new URL(s.uri).hostname.replace("www.", ""); } catch { return s.uri; } })()}
                                    </a>
                                ))}
                            </div>
                        )}
                        {bestCur.kit_id && (
                            <Btn
                                testId="best-niche-open-kit"
                                variant="green"
                                className="mt-3"
                                onClick={() => setSelected(bestCur.kit_id)}
                            >
                                <span className="flex items-center gap-2">
                                    Ver el kit de este canal <ArrowRight size={13} />
                                </span>
                            </Btn>
                        )}
                    </div>
                )}
            </Panel>

            <Panel title="Generar kit" testId="kit-form" className="mb-6">
                {fromNiche && (
                    <div
                        className="mb-3 flex items-center gap-2 border border-primary/60 p-2 text-xs text-primary"
                        data-testid="kit-from-niche-banner"
                    >
                        <StatusDot color="#00FFFF" pulse /> Generando kit desde el recomendador de nichos:
                        <span className="text-foreground">{fromNiche}</span>
                    </div>
                )}
                <div className="flex flex-col gap-3 md:flex-row">
                    <input
                        data-testid="kit-niche-input"
                        value={niche}
                        onChange={(e) => setNiche(e.target.value)}
                        placeholder="Nicho (ej: astronomía explicada simple)"
                        className="flex-1 border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                    />
                    <input
                        data-testid="kit-tone-input"
                        value={tone}
                        onChange={(e) => setTone(e.target.value)}
                        className="border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary md:w-64"
                    />
                    <Btn
                        testId="kit-generate-btn"
                        onClick={() => niche.trim() && generate.mutate()}
                        disabled={generate.isPending || !niche.trim()}
                    >
                        <span className="flex items-center gap-2">
                            <Sparkles size={14} />
                            {generate.isPending ? "Generando…" : "Generar kit"}
                        </span>
                    </Btn>
                </div>
                {kits.length > 1 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {kits.map((k) => (
                            <button
                                key={k.id}
                                data-testid={`kit-tab-${k.id}`}
                                onClick={() => setSelected(k.id)}
                                className={`border px-3 py-1 text-xs uppercase tracking-wider ${
                                    active?.id === k.id
                                        ? "border-primary text-primary"
                                        : "border-border text-muted-foreground hover:text-foreground"
                                }`}
                            >
                                {k.channel_name}
                            </button>
                        ))}
                    </div>
                )}
            </Panel>

            {active && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-testid="kit-detail">
                    <div className="lg:col-span-2">
                        <div className="border border-border bg-card">
                            <div className="relative">
                                <img
                                    src={active.banner || BANNER_PH}
                                    alt="banner"
                                    className="h-40 w-full object-cover opacity-90"
                                />
                                <img
                                    src={active.avatar || AVATAR_PH}
                                    alt="avatar"
                                    className="absolute -bottom-6 left-6 h-16 w-16 border-2 border-black object-cover"
                                />
                            </div>
                            <div className="px-6 pb-6 pt-10">
                                <div className="flex items-center gap-3">
                                    <h2 className="font-head text-2xl">{active.channel_name}</h2>
                                    <span className="text-sm text-primary">{active.handle}</span>
                                </div>
                                <p className="mt-2 text-sm text-muted-foreground">{active.description}</p>
                                {active.suggested_email && (
                                    <div
                                        className="mt-3 flex items-center gap-2 border border-border p-2 text-xs"
                                        data-testid="kit-suggested-email"
                                    >
                                        <Mail size={12} className="shrink-0 text-primary" />
                                        <span className="text-muted-foreground">Email para crear la cuenta:</span>
                                        <span className="break-all font-mono text-primary">{active.suggested_email}</span>
                                    </div>
                                )}
                                <div className="mt-4 flex flex-wrap gap-1">
                                    {(active.keywords || []).map((k, i) => (
                                        <span key={i} className="border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
                                            {k}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-5 flex gap-3">
                                    {(active.palette || []).map((p, i) => (
                                        <div key={i} className="text-center">
                                            <div className="h-10 w-10 border border-border" style={{ background: p.hex }} />
                                            <div className="mt-1 text-[9px] text-muted-foreground">{p.hex}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-3">
                            <Btn
                                testId="kit-visuals-btn"
                                variant="ghost"
                                onClick={() => visuals.mutate(active.id)}
                                disabled={visuals.isPending}
                            >
                                <span className="flex items-center gap-2">
                                    <ImageIcon size={14} />
                                    {visuals.isPending ? "Generando visuales…" : "Generar banner + avatar (IA)"}
                                </span>
                            </Btn>
                            <Btn
                                testId="kit-connect-btn"
                                variant="green"
                                onClick={() => connect.mutate(active)}
                                disabled={connect.isPending}
                            >
                                <span className="flex items-center gap-2">
                                    <LinkIcon size={14} /> Conectar canal (OAuth)
                                </span>
                            </Btn>
                            <Btn
                                testId="kit-export-btn"
                                onClick={() => exportKit.mutate(active)}
                                disabled={exportKit.isPending}
                            >
                                <span className="flex items-center gap-2">
                                    <Download size={14} />
                                    {exportKit.isPending ? "Empaquetando…" : "Exportar kit (ZIP)"}
                                </span>
                            </Btn>
                        </div>
                        {visuals.isPending && (
                            <div className="mt-3 flex items-center gap-2 text-xs text-primary">
                                <StatusDot color="#00FFFF" pulse /> Nano Banana renderizando (~15s)…
                            </div>
                        )}
                    </div>

                    <div>
                        <Panel title="Pilares de contenido">
                            <ul className="space-y-2 text-sm">
                                {(active.content_pillars || []).map((p, i) => (
                                    <li key={i} className="flex gap-2">
                                        <span className="text-primary">◢</span> {p}
                                    </li>
                                ))}
                            </ul>
                        </Panel>
                        <Panel title="Plan de títulos" className="mt-4">
                            <ol className="space-y-1.5 text-xs text-muted-foreground">
                                {(active.title_plan || []).map((t, i) => (
                                    <li key={i}>
                                        <span className="text-primary">{String(i + 1).padStart(2, "0")}</span> {t}
                                    </li>
                                ))}
                            </ol>
                        </Panel>
                    </div>
                </div>
            )}
        </div>
    );
}
